// src/theme/ThemeProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { THEMES, applyPreset, type Theme, type ThemeMode, type ThemePreset } from "./theme";

type ThemeContextValue = {
  theme: Theme;
  mode: ThemeMode;
  preset: ThemePreset;
  setMode: (m: ThemeMode) => void;
  toggleMode: () => void;
  setPreset: (p: ThemePreset) => void;
};

const KEY_MODE = "theme_v1";
const KEY_PRESET = "theme_preset_v1";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readMode(): ThemeMode {
  try {
    const v = String(window.localStorage.getItem(KEY_MODE) || "");
    if (v === "light" || v === "dark" || v === "midnight") return v;
  } catch {}
  return "dark";
}

function readPreset(): ThemePreset {
  try {
    const v = String(window.localStorage.getItem(KEY_PRESET) || "");
    if (v === "romantic" || v === "mature") return v;
  } catch {}
  return "romantic";
}

function writeKV(k: string, v: string) {
  try {
    window.localStorage.setItem(k, v);
  } catch {}
}

function applyToDOM(t: Theme) {
  const r = document.documentElement;
  r.style.setProperty("--bg", t.bg);
  r.style.setProperty("--surface", t.surface);
  r.style.setProperty("--surface2", t.surface2);
  r.style.setProperty("--text", t.text);
  r.style.setProperty("--muted", t.muted);
  r.style.setProperty("--border", t.border);
  r.style.setProperty("--primary", t.primary);
  r.style.setProperty("--primary2", t.primary2);
  r.style.setProperty("--danger", t.danger);
  r.style.setProperty("--chip", t.chip);
  r.style.setProperty("--chipText", t.chipText);
  r.style.setProperty("--shadow", t.shadow);
  r.style.setProperty("--romanticGlow", t.romanticGlow);

  // Optional: handy for debugging / custom CSS hooks
  r.setAttribute("data-theme", t.mode);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readMode());
  const [preset, setPresetState] = useState<ThemePreset>(() => readPreset());

  const theme = useMemo(() => applyPreset(THEMES[mode], preset), [mode, preset]);

  useEffect(() => {
    applyToDOM(theme);
    writeKV(KEY_MODE, mode);
    writeKV(KEY_PRESET, preset);
  }, [theme, mode, preset]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      mode,
      preset,
      setMode: (m) => setModeState(m),
      toggleMode: () => {
        const next: ThemeMode = mode === "dark" ? "light" : mode === "light" ? "midnight" : "dark";
        setModeState(next);
      },
      setPreset: (p) => setPresetState(p),
    }),
    [theme, mode, preset],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
